import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Mail, CreditCard, Palette, Users, Eye, EyeOff, Send, Save, ShieldOff, UserPlus, Shield } from "lucide-react";

const MASK = "••••••••";
const SECRET_KEYS = new Set([
  "SMTP_PASS_ADMIN", "SMTP_PASS_ACCOUNTS", "SMTP_PASS_INFO",
  "STRIPE_SECRET_KEY", "STRIPE_WEBHOOK_SECRET", "STRIPE_CONNECT_WEBHOOK_SECRET",
]);

export default function AdminSettings() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("smtp");     // smtp | stripe | branding | team
  const [settings, setSettings] = useState({});
  const [draft, setDraft] = useState({});
  const [reveal, setReveal] = useState({});
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", password: "" });
  const [promoteEmail, setPromoteEmail] = useState("");
  const [testEmail, setTestEmail] = useState({ to: "", sender: "admin" });

  const loadSettings = () =>
    api.get("/admin/settings").then(({ data }) => { setSettings(data); setDraft(data); });
  const loadTeam = () =>
    api.get("/admin/settings/team").then(({ data }) => setAdmins(data.items || []));

  useEffect(() => { loadSettings(); loadTeam(); }, []);

  const setField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const savePatch = async (keys) => {
    const patch = {};
    for (const k of keys) {
      const v = draft[k];
      // Skip the mask sentinel — user didn't retype the secret, keep existing DB value.
      if (v === MASK) continue;
      patch[k] = v ?? "";
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", patch);
      setSettings(data.settings);
      setDraft(data.settings);
      toast.success("Saved — hot-reloaded across the backend");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const sendTestEmail = async () => {
    if (!testEmail.to) return toast.error("Enter a recipient email first");
    try {
      await api.post("/admin/settings/test-email", testEmail);
      toast.success(`Test email sent to ${testEmail.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed — check SMTP settings");
    }
  };

  const addAdmin = async (e) => {
    e.preventDefault();
    if (!newAdmin.email || !newAdmin.password || !newAdmin.name) return toast.error("All fields required");
    try {
      await api.post("/admin/settings/team", newAdmin);
      toast.success(`${newAdmin.email} added as admin`);
      setNewAdmin({ email: "", name: "", password: "" });
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const promoteAdmin = async (e) => {
    e.preventDefault();
    if (!promoteEmail) return;
    try {
      await api.post("/admin/settings/team/promote", { email: promoteEmail });
      toast.success(`${promoteEmail} promoted to admin`);
      setPromoteEmail("");
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  const demoteAdmin = async (u) => {
    if (u.user_id === me?.user_id) return toast.error("You can't demote yourself");
    if (!window.confirm(`Remove admin rights from ${u.email}?`)) return;
    try {
      await api.post(`/admin/settings/team/${u.user_id}/demote`);
      toast.success("Admin rights removed");
      loadTeam();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div>
      <div className="overline text-safety">Admin</div>
      <h1 className="mt-1 font-display font-black text-3xl tracking-tighter text-forest">Platform settings</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Change SMTP, Stripe, branding, and team without touching `.env`. Changes hot-reload immediately.
      </p>

      {/* Tabs */}
      <div className="mt-6 flex flex-wrap gap-2 border-b border-border">
        {[
          { id: "smtp", label: "SMTP", icon: Mail },
          { id: "stripe", label: "Stripe", icon: CreditCard },
          { id: "branding", label: "Branding", icon: Palette },
          { id: "team", label: "Team", icon: Users },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`settings-tab-${t.id}`}
            className={`px-4 py-2 overline text-xs flex items-center gap-2 ${tab === t.id ? "border-b-2 border-forest text-forest" : "text-muted-foreground"}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "smtp" && (
        <SmtpTab draft={draft} setField={setField} reveal={reveal} setReveal={setReveal}
                 savePatch={savePatch} saving={saving} testEmail={testEmail} setTestEmail={setTestEmail}
                 sendTestEmail={sendTestEmail} />
      )}
      {tab === "stripe" && (
        <StripeTab draft={draft} setField={setField} reveal={reveal} setReveal={setReveal}
                   savePatch={savePatch} saving={saving} />
      )}
      {tab === "branding" && (
        <BrandingTab draft={draft} setField={setField} savePatch={savePatch} saving={saving} />
      )}
      {tab === "team" && (
        <TeamTab admins={admins} me={me} newAdmin={newAdmin} setNewAdmin={setNewAdmin}
                 promoteEmail={promoteEmail} setPromoteEmail={setPromoteEmail}
                 addAdmin={addAdmin} promoteAdmin={promoteAdmin} demoteAdmin={demoteAdmin} />
      )}
    </div>
  );
}

// ---------- Reusable pieces ----------
function Field({ label, hint, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="overline text-muted-foreground mb-2">{label}</div>
      {children}
      {hint && <div className="mt-1 text-[11px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function SecretInput({ value, onChange, reveal, onToggleReveal, testid }) {
  const isMask = value === MASK;
  return (
    <div className="relative">
      <Input
        type={reveal || !isMask ? "text" : "password"}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        onFocus={(e) => { if (isMask) { onChange(""); e.target.select(); } }}
        onBlur={(e) => { if (e.target.value === "") onChange(MASK); }}
        placeholder="•••• leave to keep unchanged"
        data-testid={testid}
        className="rounded-none h-11 pr-10 font-mono text-xs"
      />
      <button type="button" onClick={onToggleReveal}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-forest">
        {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function SaveBar({ onSave, saving }) {
  return (
    <div className="pt-6 mt-6 border-t border-border flex justify-end">
      <Button onClick={onSave} disabled={saving} data-testid="settings-save-btn"
        className="rounded-none bg-forest hover:bg-forest-800 text-white h-11 px-6">
        <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save & hot-reload"}
      </Button>
    </div>
  );
}

// ---------- SMTP ----------
function SmtpTab({ draft, setField, reveal, setReveal, savePatch, saving, testEmail, setTestEmail, sendTestEmail }) {
  const smtpKeys = [
    "SMTP_HOST", "SMTP_PORT", "SMTP_USE_SSL",
    "SMTP_FROM_ADMIN", "SMTP_PASS_ADMIN",
    "SMTP_FROM_ACCOUNTS", "SMTP_PASS_ACCOUNTS",
    "SMTP_FROM_INFO", "SMTP_PASS_INFO",
  ];
  return (
    <div className="mt-6 border border-border bg-white p-6 space-y-5">
      <div className="font-display text-xl text-forest">SMTP configuration</div>
      <p className="text-sm text-muted-foreground">Server settings + three sender mailboxes (admin@ for system emails, accounts@ for receipts, info@ for support).</p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Field label="Host"><Input value={draft.SMTP_HOST ?? ""} onChange={(e) => setField("SMTP_HOST", e.target.value)} data-testid="smtp-host" placeholder="mail.example.com" className="rounded-none h-11" /></Field>
        <Field label="Port"><Input value={draft.SMTP_PORT ?? ""} onChange={(e) => setField("SMTP_PORT", e.target.value)} data-testid="smtp-port" placeholder="465" className="rounded-none h-11" /></Field>
        <Field label="Use SSL">
          <select value={draft.SMTP_USE_SSL ?? "true"} onChange={(e) => setField("SMTP_USE_SSL", e.target.value)} data-testid="smtp-ssl" className="w-full h-11 rounded-none border border-border px-3">
            <option value="true">true (SSL/TLS)</option>
            <option value="false">false (plain)</option>
          </select>
        </Field>
      </div>

      {[
        { role: "admin", label: "Admin mailbox (system emails)" },
        { role: "accounts", label: "Accounts mailbox (receipts, invoicing)" },
        { role: "info", label: "Info mailbox (support, contact)" },
      ].map(({ role, label }) => {
        const fk = `SMTP_FROM_${role.toUpperCase()}`;
        const pk = `SMTP_PASS_${role.toUpperCase()}`;
        return (
          <div key={role} className="border-t border-border pt-4">
            <div className="overline text-xs text-safety mb-2">{label}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="From address"><Input value={draft[fk] ?? ""} onChange={(e) => setField(fk, e.target.value)} data-testid={`smtp-from-${role}`} className="rounded-none h-11" /></Field>
              <Field label="Password (write-only)" hint="Leave as •••• to keep the existing password.">
                <SecretInput value={draft[pk] ?? ""} onChange={(v) => setField(pk, v)}
                             reveal={!!reveal[pk]} onToggleReveal={() => setReveal((r) => ({ ...r, [pk]: !r[pk] }))}
                             testid={`smtp-pass-${role}`} />
              </Field>
            </div>
          </div>
        );
      })}

      <div className="border-t border-border pt-4">
        <div className="overline text-xs text-safety mb-2">Send test email</div>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,180px,auto] gap-3 items-end">
          <Field label="Recipient"><Input type="email" value={testEmail.to} onChange={(e) => setTestEmail({ ...testEmail, to: e.target.value })} data-testid="smtp-test-to" placeholder="you@company.com" className="rounded-none h-11" /></Field>
          <Field label="From mailbox">
            <select value={testEmail.sender} onChange={(e) => setTestEmail({ ...testEmail, sender: e.target.value })} data-testid="smtp-test-sender" className="w-full h-11 rounded-none border border-border px-3">
              <option value="admin">admin@</option>
              <option value="accounts">accounts@</option>
              <option value="info">info@</option>
            </select>
          </Field>
          <Button onClick={sendTestEmail} data-testid="smtp-test-btn" variant="outline" className="rounded-none h-11">
            <Send className="w-4 h-4 mr-2" /> Send test
          </Button>
        </div>
      </div>

      <SaveBar onSave={() => savePatch(smtpKeys)} saving={saving} />
    </div>
  );
}

// ---------- Stripe ----------
function StripeTab({ draft, setField, reveal, setReveal, savePatch, saving }) {
  const stripeKeys = ["STRIPE_SECRET_KEY", "STRIPE_PUBLISHABLE_KEY", "STRIPE_WEBHOOK_SECRET", "STRIPE_CONNECT_WEBHOOK_SECRET"];
  return (
    <div className="mt-6 border border-border bg-white p-6 space-y-5">
      <div className="font-display text-xl text-forest">Stripe keys</div>
      <p className="text-sm text-muted-foreground">
        Get these from <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="underline text-forest">dashboard.stripe.com/apikeys</a> and <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noreferrer" className="underline text-forest">/webhooks</a>. Secret keys are write-only — the current value is masked.
      </p>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Secret key (sk_test_… or sk_live_…)" hint="Server-side charges. Never expose to the browser.">
          <SecretInput value={draft.STRIPE_SECRET_KEY ?? ""} onChange={(v) => setField("STRIPE_SECRET_KEY", v)}
                       reveal={!!reveal.STRIPE_SECRET_KEY} onToggleReveal={() => setReveal((r) => ({ ...r, STRIPE_SECRET_KEY: !r.STRIPE_SECRET_KEY }))}
                       testid="stripe-secret" />
        </Field>
        <Field label="Publishable key (pk_test_… or pk_live_…)" hint="Safe to expose in the browser.">
          <Input value={draft.STRIPE_PUBLISHABLE_KEY ?? ""} onChange={(e) => setField("STRIPE_PUBLISHABLE_KEY", e.target.value)} data-testid="stripe-publishable" className="rounded-none h-11 font-mono text-xs" />
        </Field>
        <Field label="Payments webhook secret (whsec_…)" hint="Verifies /api/stripe/webhook + /api/webhook/stripe.">
          <SecretInput value={draft.STRIPE_WEBHOOK_SECRET ?? ""} onChange={(v) => setField("STRIPE_WEBHOOK_SECRET", v)}
                       reveal={!!reveal.STRIPE_WEBHOOK_SECRET} onToggleReveal={() => setReveal((r) => ({ ...r, STRIPE_WEBHOOK_SECRET: !r.STRIPE_WEBHOOK_SECRET }))}
                       testid="stripe-webhook" />
        </Field>
        <Field label="Connect webhook secret (whsec_…)" hint="Verifies /api/webhook/stripe/connect (account.updated events).">
          <SecretInput value={draft.STRIPE_CONNECT_WEBHOOK_SECRET ?? ""} onChange={(v) => setField("STRIPE_CONNECT_WEBHOOK_SECRET", v)}
                       reveal={!!reveal.STRIPE_CONNECT_WEBHOOK_SECRET} onToggleReveal={() => setReveal((r) => ({ ...r, STRIPE_CONNECT_WEBHOOK_SECRET: !r.STRIPE_CONNECT_WEBHOOK_SECRET }))}
                       testid="stripe-connect-webhook" />
        </Field>
      </div>

      <SaveBar onSave={() => savePatch(stripeKeys)} saving={saving} />
    </div>
  );
}

// ---------- Branding ----------
function BrandingTab({ draft, setField, savePatch, saving }) {
  const keys = ["SITE_URL", "ADMIN_ALERT_EMAIL", "FROM_DISPLAY_NAME"];
  return (
    <div className="mt-6 border border-border bg-white p-6 space-y-5">
      <div className="font-display text-xl text-forest">Branding & addresses</div>
      <p className="text-sm text-muted-foreground">Everywhere the app builds a link back to itself (email templates, Stripe success URLs, password reset), it uses these values.</p>

      <div className="grid grid-cols-1 gap-4">
        <Field label="Site URL" hint="Used in email links & Stripe success/cancel URLs. E.g. https://furnitureexchange.co.uk">
          <Input value={draft.SITE_URL ?? ""} onChange={(e) => setField("SITE_URL", e.target.value)} data-testid="branding-site-url" placeholder="https://furnitureexchange.co.uk" className="rounded-none h-11" />
        </Field>
        <Field label="Admin alert email" hint="Where new-verification + report notifications are sent.">
          <Input type="email" value={draft.ADMIN_ALERT_EMAIL ?? ""} onChange={(e) => setField("ADMIN_ALERT_EMAIL", e.target.value)} data-testid="branding-admin-email" placeholder="admin@furnitureexchange.co.uk" className="rounded-none h-11" />
        </Field>
        <Field label="Email From display name" hint='Shown in inboxes: "<From-Name> <address>". Default: "Furniture Exchange".'>
          <Input value={draft.FROM_DISPLAY_NAME ?? ""} onChange={(e) => setField("FROM_DISPLAY_NAME", e.target.value)} data-testid="branding-from-name" placeholder="Furniture Exchange" className="rounded-none h-11" />
        </Field>
      </div>

      <SaveBar onSave={() => savePatch(keys)} saving={saving} />
    </div>
  );
}

// ---------- Team ----------
function TeamTab({ admins, me, newAdmin, setNewAdmin, promoteEmail, setPromoteEmail, addAdmin, promoteAdmin, demoteAdmin }) {
  return (
    <div className="mt-6 space-y-6">
      <div className="border border-border bg-white p-6">
        <div className="font-display text-xl text-forest mb-3">Add a new admin</div>
        <form onSubmit={addAdmin} data-testid="team-add-form" className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Input required value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Name" data-testid="team-add-name" className="rounded-none h-11" />
          <Input required type="email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="email@company.com" data-testid="team-add-email" className="rounded-none h-11" />
          <div className="flex gap-2">
            <Input required type="password" value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="Password" data-testid="team-add-password" className="rounded-none h-11" />
            <Button type="submit" data-testid="team-add-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-11">
              <UserPlus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </form>
      </div>

      <div className="border border-border bg-white p-6">
        <div className="font-display text-xl text-forest mb-3">Promote an existing user</div>
        <form onSubmit={promoteAdmin} className="flex gap-3" data-testid="team-promote-form">
          <Input required type="email" value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)} placeholder="existing.user@example.com" data-testid="team-promote-email" className="rounded-none h-11 max-w-md" />
          <Button type="submit" data-testid="team-promote-btn" variant="outline" className="rounded-none h-11">
            <Shield className="w-4 h-4 mr-1" /> Promote to admin
          </Button>
        </form>
      </div>

      <div className="border border-border bg-white">
        <div className="p-4 overline text-muted-foreground border-b border-border">Current admins ({admins.length})</div>
        {admins.map((u) => (
          <div key={u.user_id} data-testid={`team-row-${u.user_id}`} className="p-4 grid grid-cols-[1fr,auto,auto] gap-4 items-center border-t border-border first:border-t-0">
            <div>
              <div className="font-display text-sm">{u.name}{u.user_id === me?.user_id ? " (you)" : ""}</div>
              <div className="text-xs text-muted-foreground">{u.email}</div>
            </div>
            {u.is_suspended
              ? <Badge className="rounded-none bg-destructive text-white overline text-[9px]">suspended</Badge>
              : <Badge className="rounded-none bg-forest text-white overline text-[9px]">admin</Badge>}
            <Button
              onClick={() => demoteAdmin(u)}
              disabled={u.user_id === me?.user_id}
              size="sm" variant="outline"
              data-testid={`team-demote-${u.user_id}`}
              className="rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white">
              <ShieldOff className="w-4 h-4 mr-1" /> Remove
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
